import React, { useEffect } from 'react';
import SoundLibraryExplorerSlider from '../components/SoundLibraryExplorerSlider';
import { useSEO, seoConfigs } from '../hooks/useSEO';

export const SoundLibraryExplorer: React.FC = () => {
  useSEO(seoConfigs.soundLibraryExplorer);

  const callToAction = (
    <div className="mt-8 text-center">
      <a
        href="https://sangampanta.gumroad.com/l/soundlibraryexplorer?wanted=true"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded-full text-white bg-gradient-to-r from-sky-500 via-indigo-500 to-purple-600 hover:from-sky-400 hover:via-indigo-400 hover:to-purple-500 transition-colors duration-300 shadow-[0_10px_40px_rgba(56,189,248,0.35)]"
      >
        Get SoundLibraryExplorer
        <svg className="ml-3 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </a>
      <p className="mt-3 text-sm text-gray-400">One-time purchase · Instant macOS download · Lifetime access</p>
    </div>
  );

  const keyFeatures = [
    'Smart audio organization with automatic tagging for WAV, MP3, AIFF, FLAC, and M4A files',
    'Advanced metadata search filtered by bit depth, sample rate, duration, and more',
    'Visual waveform preview for instant scanning',
    'Batch processing for importing and cleaning entire folders',
    'Duplicate detection to keep libraries lean',
    'Native macOS experience optimized for Apple Silicon (works on Intel too)',
    'Fast performance once database caching completes',
  ];

  const idealFor = ['Sound Designers', 'Musicians', 'Podcasters', 'Video Editors', 'Game Developers', 'Audio Engineers'];

  const whyChoose = [
    'No subscription — one-time purchase with lifetime updates',
    'Privacy-first — your files never leave your Mac',
    'Lightweight footprint with minimal system overhead',
    'Intuitive interface — start organizing in seconds',
  ];

  const technicalSpecs = [
    'Supports WAV, MP3, AIFF, FLAC, M4A formats',
    'Displays bit depth, sample rate, channels, duration, and file size',
    'macOS 15 Sonoma and newer recommended (Apple Silicon preferred, Intel supported)',
    'Freesound.org integration for online sound library search',
    'Offline-first design — no internet connection required for local library management',
  ];

  const gatekeeperSteps = [
    {
      title: 'Try to open the app',
      details:
        'Double-click SoundLibraryExplorer.app. macOS will warn that it is from an unidentified developer — this is expected because the app is not signed with a paid Apple Developer account.',
    },
    {
      title: 'Open System Settings',
      details: 'Go to  > System Settings (or System Preferences on older macOS versions).',
    },
    {
      title: 'Privacy & Security',
      details: 'Select Privacy & Security in the sidebar. Scroll to the Security section.',
    },
    {
      title: 'Allow Anyway',
      details: 'Click “Allow Anyway” next to the message about SoundLibraryExplorer being blocked.',
    },
    {
      title: 'Open once more',
      details:
        'Double-click the app again. When the new prompt appears (“macOS cannot verify the developer…”), choose Open. You will not need to repeat these steps again for this Mac.',
    },
  ];

  return (
    <div className="min-h-screen text-gray-100 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-12">
        <section className="text-center space-y-4">
          <p className="uppercase tracking-[0.4em] text-xs text-sky-400/80">Utilities · macOS App</p>
          <h1 className="text-4xl md:text-5xl font-semibold text-white">
            SoundLibraryExplorer
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            Organize, search, and audition massive sound libraries with cinematic precision—no subscriptions, no data collection,
            just a fast, native macOS experience built for audio pros.
          </p>
          {callToAction}
        </section>

        <SoundLibraryExplorerSlider />

        <section className="bg-zinc-900/60 border border-zinc-800 backdrop-blur rounded-3xl p-8 lg:p-10 shadow-[0_25px_60px_rgba(2,6,23,0.85)]">
          <div className="grid lg:grid-cols-2 gap-10">
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-sky-300">Meet Your Audio Library</h2>
              <p className="text-gray-300 leading-relaxed">
                Designed for sound designers, mixers, and storytellers who live inside their libraries. SoundLibraryExplorer brings
                intelligent tagging, lightning-fast search, and waveform previews into one distraction-free workspace, so you can focus
                on creative decisions instead of file hunting.
              </p>
              <ul className="space-y-3">
                {keyFeatures.map((feature) => (
                  <li key={feature} className="flex items-start text-sm md:text-base text-gray-200">
                    <span className="mr-3 mt-1 h-2 w-2 rounded-full bg-sky-400" aria-hidden />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-zinc-800/80 to-zinc-900/90 border border-zinc-800 rounded-2xl p-6 flex flex-col justify-between shadow-[0_30px_80px_rgba(0,0,0,0.55)]">
              <div className="space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Perfect for</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {idealFor.map((role) => (
                      <span key={role} className="text-sm text-gray-200 bg-zinc-900/70 border border-zinc-800 rounded-full px-4 py-2">
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Why creators switch</p>
                  <ul className="mt-3 space-y-2 text-gray-200 text-sm">
                    {whyChoose.map((reason) => (
                      <li key={reason} className="flex items-start">
                        <svg className="h-4 w-4 text-emerald-400 mt-1 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                *Performance note: the first database scan caches waveforms and metadata. Once completed, searches happen in milliseconds.
              </p>
            </div>
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Technical Specs</p>
            <ul className="mt-4 space-y-3 text-gray-200">
              {technicalSpecs.map((spec) => (
                <li key={spec} className="flex items-start text-sm">
                  <span className="mr-3 mt-1 h-1.5 w-1.5 rounded-full bg-indigo-400" />
                  {spec}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Included tools</p>
            <ul className="mt-4 space-y-3 text-gray-200 text-sm">
              <li>• Batch renamer with scene / reel presets</li>
              <li>• Smart bins for Dialogue, Ambience, FX, Music, Foley</li>
              <li>• Favorite + star ratings synced per project</li>
              <li>• Waveform loupe for fast detail preview</li>
              <li>• Offline-first database with instant recall</li>
            </ul>
          </div>
        </section>

        <section className="bg-zinc-900/70 border border-zinc-800 rounded-3xl p-8 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gray-400">macOS Gatekeeper</p>
              <h3 className="text-2xl font-semibold text-white">How to open the unsigned app safely</h3>
              <p className="text-gray-300 mt-2 max-w-2xl">
                SoundLibraryExplorer is distributed without a paid Apple Developer signature, so macOS will ask for manual approval the
                first time. Follow these official Apple-approved steps and you will only need to do it once per Mac.
              </p>
            </div>
            <a
              href="https://support.apple.com/guide/mac-help/open-a-mac-app-from-an-unidentified-developer-mchleab3a043/mac"
              target="_blank"
              rel="noreferrer"
              className="text-sm text-sky-400 hover:text-sky-300"
            >
              Apple Support reference ↗
            </a>
          </div>
          <ol className="space-y-4 text-gray-200">
            {gatekeeperSteps.map((step, index) => (
              <li key={step.title} className="flex gap-4">
                <div className="text-lg font-semibold text-sky-300">{index + 1}.</div>
                <div>
                  <p className="font-medium text-white">{step.title}</p>
                  <p className="text-sm text-gray-300 mt-1">{step.details}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="text-sm text-emerald-300/80 bg-emerald-900/10 border border-emerald-800 rounded-2xl p-4">
            ✔️ After you click <strong>Open</strong>, macOS remembers your decision. Future launches are immediate.
          </p>
          <p className="text-xs text-gray-400">
            Privacy assurance: SoundLibraryExplorer never collects, transmits, or shares data. Every audio file, database entry, and
            preference stays entirely on your Mac.
          </p>
        </section>

        <section className="text-center space-y-4">
          <h2 className="text-3xl font-semibold">Transform your sonic workflow today</h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Built by an audio post professional who manages film, episodic, and immersive projects daily. This is the same toolkit used
            on Sangam’s Dolby Atmos mixes and broadcast deliveries—now available for your studio.
          </p>
          {callToAction}
        </section>
      </div>
    </div>
  );
}

export default SoundLibraryExplorer;
